/* global Lang */

import React, { useMemo } from 'react';
import { ThemeProvider } from 'styled-components';
import { useApiGet } from '../../../common/services/api';
import PlayerActivity from '../../components/PlayerActivity';
import activityTransformer from '../../transformers/activityTransformer';
import userTransformer from '../../transformers/userTransformer';
import theme from '../../../game/styles/theme';
import PlayerDashboardHeader from '../PlayerDashboardHeader';
import PlayerDomainHeroes from '../../components/PlayerDomainHeroes';
import powerTransformer from '../../../game/transformers/power';
import EstimationGraph from '../../components/EstimationGraph';
import Card, { CardBody, CardTitle, CardText } from '../../components/Card';
import ApiResult from '../../components/ApiResult';
import DomainInfo from '../../components/DomainInfo';

const PlayerStats = ({ userId, ...headerProps }) => {
  const id = userId || '';
  const { response: user, isLoading: userLoading, error: userError } = useApiGet({
    url: `users/${id}`,
    transformer: userTransformer,
  });
  const {
    response: activities = [],
    isLoading: activitiesLoading,
    error: activitiesError,
  } = useApiGet({
    url: `users/${id}/activities`,
    transformer: activityTransformer,
  });
  const { response: powers = [], isLoading: powersLoading, error: powersError } = useApiGet({
    url: 'powers',
    transformer: powerTransformer,
  });
  const riasocDomains = useMemo(
    () => !userLoading && !userError && user && user.domains.filter(domain => !domain.hero),
    [user, userLoading, userError],
  );

  return (
    <ThemeProvider theme={theme}>
      <>
        <PlayerDashboardHeader
          loading={userLoading}
          error={userError}
          user={user}
          showHowTo
          {...headerProps}
        />

        <Card className="mb-4">
          <CardBody>
            <CardTitle>{Lang.get('messages.dashboard.disclaimer.title')}</CardTitle>
            <CardText>{Lang.get('messages.dashboard.disclaimer.description')}</CardText>
          </CardBody>
        </Card>

        <h3 className="bg-body">{Lang.get('messages.dashboard.activitiesTop10')}</h3>
        <ApiResult
          error={userError || activitiesError}
          loading={userLoading || activitiesLoading}
          render={() => (
            <div className="row">
              {activities.slice(0, 10).map(({ id: questionId, ...activity }, index) => (
                <PlayerActivity
                  key={questionId}
                  index={index + 1}
                  {...activity}
                  fullDomains={user.domains}
                />
              ))}
            </div>
          )}
        />
        <div className="row">
          <div className="col-md-6">
            <h3 className="bg-body">{Lang.get('messages.dashboard.heroes')}</h3>
            <ApiResult
              error={userError || powersError}
              loading={userLoading || powersLoading}
              render={() => <PlayerDomainHeroes {...user} allPowers={powers} />}
            />
          </div>
          <div className="col-md-6">
            <h3 className="bg-body">{Lang.get('messages.dashboard.riasoc')}</h3>
            <ApiResult
              error={userError}
              loading={userLoading}
              render={() => (
                <div className="position-relative">
                  <EstimationGraph estimations={user.estimations} domains={riasocDomains} />
                  <DomainInfo domains={riasocDomains} />
                </div>
              )}
            />
          </div>
        </div>
      </>
    </ThemeProvider>
  );
};

export default PlayerStats;
